import {
  fetchOrderDeliveryDetails,
  type DashboardExperienceOrderDeliveryDetailsResponse,
} from '@library/api-dashboard-experience'
import { datadogLogs } from '@datadog/browser-logs'
import { datadogRum } from '@datadog/browser-rum'
import { useEffect, useRef } from 'react'
import useSWR, { type SWRConfiguration } from 'swr'

type UseOrderDeliveryDetailsResult = {
  orderDeliveryDetails: DashboardExperienceOrderDeliveryDetailsResponse | undefined
  error: Error | undefined
  isLoading: boolean
}

const initialState: UseOrderDeliveryDetailsResult = {
  orderDeliveryDetails: undefined,
  error: undefined,
  isLoading: false,
}

const getStatusCode = (error: unknown): number | undefined => {
  if (typeof error === 'object' && error !== null && 'status' in error) {
    const status = (error as { status?: unknown }).status

    if (typeof status === 'number') {
      return status
    }
  }

  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = (error as { message?: unknown }).message

    if (typeof message === 'string') {
      // Match common HTTP error formats such as "status=400", "status: 400", and "with 401".
      const statusMatch = message.match(/(?:status=|status(?: code)?:\s*|with\s)(\d{3})/i)

      if (statusMatch) {
        return Number(statusMatch[1])
      }
    }
  }

  return undefined
}

const getErrorDetailsForTracking = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message
  }

  return JSON.stringify(error)
}

type OrderDeliveryDetailsSwrConfiguration = SWRConfiguration<
  DashboardExperienceOrderDeliveryDetailsResponse,
  Error
>

const retryOrderDeliveryDetailsError: NonNullable<
  OrderDeliveryDetailsSwrConfiguration['onErrorRetry']
> = (error, _key, config, revalidate, revalidateOptions) => {
  if (getStatusCode(error) === 401) {
    return
  }

  const maxRetryCount = config.errorRetryCount

  if (maxRetryCount !== undefined && revalidateOptions.retryCount > maxRetryCount) {
    return
  }

  const retryExponent = Math.min(revalidateOptions.retryCount, 8)
  const retryDelay =
    Math.floor((Math.random() + 0.5) * 2 ** retryExponent) * config.errorRetryInterval

  setTimeout(revalidate, retryDelay, revalidateOptions)
}

const orderDeliveryDetailsSwrConfig: OrderDeliveryDetailsSwrConfiguration = {
  errorRetryInterval: 5000,
  onErrorRetry: retryOrderDeliveryDetailsError,
}

const useOrderDeliveryDetails = (
  orderId?: string,
  enabled: boolean = true,
): UseOrderDeliveryDetailsResult => {
  const swrKey = orderId && enabled ? orderId : null
  const { data, error } = useSWR(swrKey, fetchOrderDeliveryDetails, orderDeliveryDetailsSwrConfig)
  const trackedErrorKey = useRef<string | null>(null)
  const statusCode = getStatusCode(error)
  const errorMessage = error
    ? `Error fetching order delivery details in myHome ${getErrorDetailsForTracking(error)}`
    : undefined
  const orderIdForTracking = swrKey ?? 'no-order'
  const statusCodeForTracking = statusCode ?? 'unknown'
  const errorTrackingKey = errorMessage
    ? `${orderIdForTracking}:${statusCodeForTracking}:${errorMessage}`
    : null

  useEffect(() => {
    if (!enabled) {
      return
    }

    if (!error || !errorMessage || !errorTrackingKey || !orderId) {
      trackedErrorKey.current = null

      return
    }

    if (trackedErrorKey.current === errorTrackingKey) {
      return
    }

    trackedErrorKey.current = errorTrackingKey
    datadogLogs.logger.error(errorMessage)
    datadogRum.addError(error, {
      page: 'MyHomePage',
      errorMessage: errorMessage,
    })
    if (statusCode && statusCode !== 200) {
      datadogRum.addAction('order_delivery_details_api_error', {
        page: 'MyHomePage',
        statusCode,
      })
    }
  }, [enabled, error, errorMessage, errorTrackingKey, orderId, statusCode])

  if (!orderId || !enabled) {
    return initialState
  }

  if (error) {
    return {
      ...initialState,
      error,
      isLoading: false,
    }
  }

  if (!data) {
    return {
      ...initialState,
      isLoading: true,
    }
  }

  return {
    orderDeliveryDetails: data,
    error,
    isLoading: false,
  }
}

export { useOrderDeliveryDetails }
